import React, { useState, useMemo, useEffect, useCallback } from "react";
import AddIcon from "@mui/icons-material/Add";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useDispatch, useSelector } from "react-redux";
import { useSnackbar } from "notistack";
import AdminLoader from "../../components/common/loader/AdminLoader";
import {
  createOrUpdateInventoryData,
  deleteInventoryData,
  getInventoryByCreatedBy,
} from "../../store/slices/InventorySlice";
import { Trash2, X } from "lucide-react";
import { useLoggedUser } from "../../utils/useLoggedUser";
import { getFeatureAccessLevel, hasViewOrFullAccess } from "../../utils/featureAccessUtils";
import { getAllAssemblyInventory } from "../../store/slices/assemblyInventorySlice";
import { fetchHardware } from "../../store/slices/hardwareSlice";
import { setCurrentPageTitle } from "../../store/slices/pageSlice";
import { fetchAllProjects } from "../../store/slices/projectsSlice";
import { fetchHardwareDescriptions } from "../../store/slices/hardwareDescriptionSlice";
import { fetchEmployees } from "../../store/slices/employeeSlice";

const isMongoObjectIdString = (value) =>
  typeof value === "string" && /^[a-f0-9]{24}$/i.test(value.trim());

/** Normalize project id from backend/UI projectDetails (supports populated projectid object). */
const extractProjectIdFromDetail = (pd) => {
  if (!pd) return null;
  const rawProjectRef = pd.projectid ?? pd.projectId;
  if (rawProjectRef && typeof rawProjectRef === "object") {
    return (
      rawProjectRef._id ??
      rawProjectRef.id ??
      rawProjectRef.projectId ??
      rawProjectRef.projectid ??
      null
    );
  }
  if (rawProjectRef != null && String(rawProjectRef).trim() !== "") {
    return rawProjectRef;
  }
  if (pd.projectid == null && pd.projectId == null && pd._id != null) {
    return pd._id;
  }
  return null;
};

/** Resolve display name; skip values that are just the raw project id / ObjectId string. */
const resolveProjectDisplayName = (projectId, projectsList, fallbackNames = []) => {
  const idStr = projectId != null ? String(projectId).trim() : "";
  for (const name of fallbackNames) {
    const n = name != null ? String(name).trim() : "";
    if (!n) continue;
    if (idStr && n === idStr) continue;
    if (idStr && isMongoObjectIdString(n) && isMongoObjectIdString(idStr) && n === idStr) continue;
    return n;
  }
  if (!idStr) return "";
  const list = projectsList || [];
  const fromGlobal = list.find(
    (p) =>
      String(p._id) === idStr ||
      (p.quotationId != null && String(p.quotationId) === idStr) ||
      (p.QuotationId != null && String(p.QuotationId) === idStr) ||
      (p.projectId != null && String(p.projectId) === idStr) ||
      (p.projectid != null && String(p.projectid) === idStr)
  );
  if (!fromGlobal) return "";
  return fromGlobal.ProjectName || fromGlobal.projectName || fromGlobal.name || "";
};

// Block Units Dropdown Component
const BlockUnitsDropdown = ({
  rowIndex,
  row,
  assemblyName,
  projectDetails,
  availableProjects,
  onProjectSelect,
  onProjectRemove,
  allRowsProjectDetails, // All projectDetails from all rows to prevent duplicates
  projectsList = [],
  disabled: dropdownDisabled = false, // When true, freeze entry (no selection)
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = React.useRef(null);
  const buttonRef = React.useRef(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0, openUpward: false, maxHeight: 240 });

  // UI helper: allow wrapping at hyphens as well as spaces for long names.
  const formatProjectNameForWrap = (name) => {
    const safeName = name == null ? "" : String(name);
    return safeName.replace(/-/g, "-\u200B"); // add zero-width space after hyphen
  };

  // Calculate dropdown position when opening - keep list within viewport so it stays visible and scrollable
  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const buttonRect = buttonRef.current.getBoundingClientRect();
      const gap = 8;
      const preferredHeight = 240;
      const spaceBelow = window.innerHeight - buttonRect.bottom - gap;
      const spaceAbove = buttonRect.top - gap;
      const openUpward = spaceBelow < Math.min(preferredHeight, spaceAbove);
      const maxHeight = openUpward
        ? Math.max(120, Math.min(preferredHeight, spaceAbove - gap))
        : Math.max(120, Math.min(preferredHeight, spaceBelow - gap));
      setDropdownPosition({
        top: openUpward
          ? buttonRect.top + window.scrollY - maxHeight - 4
          : buttonRect.bottom + window.scrollY + 4,
        left: buttonRect.left + window.scrollX,
        width: buttonRect.width,
        openUpward,
        maxHeight,
      });
    }
  }, [isOpen]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target) &&
          buttonRef.current && !buttonRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      // Close only on page scroll (not when scrolling inside the dropdown list)
      const handleScroll = (e) => {
        if (dropdownRef.current && dropdownRef.current.contains(e.target)) return;
        setIsOpen(false);
      };
      window.addEventListener("scroll", handleScroll, true);
      
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
        window.removeEventListener("scroll", handleScroll, true);
      };
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  // Get selected project IDs from current row (use projectId or quotationId for dedup within row)
  const currentRowSelectedIds = projectDetails.map((p) => p.projectId || p._id);
  const currentRowSelectedIdsStr = new Set(currentRowSelectedIds.map((id) => String(id)));
  // Also treat quotationId as selected in this row when present (one project can be in multiple assembly rows)
  projectDetails.forEach((p) => {
    const qid = p.quotationId;
    if (qid != null) currentRowSelectedIdsStr.add(String(qid));
  });

  // Filter available projects: exclude only projects already selected in THIS row (same project can appear in multiple rows for different assemblies)
  const unselectedProjects = availableProjects.filter(
    (p) => {
      const projectId = p.projectId || p._id;
      const idStr = projectId != null ? String(projectId) : null;
      const qidStr = p.quotationId != null ? String(p.quotationId) : null;
      if (currentRowSelectedIdsStr.has(idStr) || (qidStr && currentRowSelectedIdsStr.has(qidStr))) return false;
      return true;
    }
  );

  const totalBlockedUnits = projectDetails.reduce(
    (sum, p) => sum + (Number(p.quantity) || 0),
    0
  );

  // Debug logging
  useEffect(() => {
    if (projectDetails && projectDetails.length > 0) {
      console.log("BlockUnitsDropdown - projectDetails:", {
        rowIndex,
        assemblyName,
        projectDetailsCount: projectDetails.length,
        projectDetails: projectDetails,
        projectNames: projectDetails.map(p => p.projectName),
      });
    }
  }, [projectDetails, rowIndex, assemblyName]);

  const getDetailDisplayName = (p) => {
    const id = extractProjectIdFromDetail(p) ?? p.projectId ?? p._id;
    const populatedName =
      typeof p.projectid === "object" && p.projectid != null
        ? p.projectid.ProjectName || p.projectid.projectName || p.projectid.name
        : "";
    const fromAvailable = (availableProjects || []).find(
      (ap) =>
        id != null &&
        (String(ap.projectId || ap._id) === String(id) ||
          (ap.quotationId != null && String(ap.quotationId) === String(id)))
    );
    const name = resolveProjectDisplayName(id, projectsList, [
      populatedName,
      p.projectName,
      p.name,
      p.ProjectName,
      fromAvailable?.projectName,
      fromAvailable?.ProjectName,
    ]);
    return name || (id != null ? `Project (${id})` : "");
  };

  // Get project names for display (backend may store _id only; projectName resolved on load)
  const projectNames = projectDetails && projectDetails.length > 0
    ? projectDetails
        .filter(p => {
          const hasValidId = !!(extractProjectIdFromDetail(p) || p.projectId || p._id);
          const hasValidQuantity = (Number(p.quantity) || 0) > 0;
          return hasValidId && hasValidQuantity;
        })
        .map((p) => getDetailDisplayName(p))
        .filter(Boolean)
    : [];

  // When row is saved (frozen), allow opening dropdown only to view multiple saved projects
  const isViewOnlySaved = dropdownDisabled && projectDetails.length > 1;
  const canOpenDropdown = !dropdownDisabled || isViewOnlySaved;

  return (
    <div className="p-2 border-b border-gray-300 flex-1 flex justify-center items-center text-center relative">
      <div className="w-full relative">
        <button
          ref={buttonRef}
          type="button"
          onClick={() => canOpenDropdown && assemblyName && setIsOpen(!isOpen)}
          className={`w-full border text-xs border-gray-400 rounded-md p-2 shadow-md text-start overflow-hidden outline-none flex items-center justify-between ${
            dropdownDisabled && !isViewOnlySaved
              ? "bg-gray-100 cursor-not-allowed text-gray-600"
              : isViewOnlySaved
              ? "bg-gray-100 cursor-pointer hover:bg-gray-200 text-gray-700"
              : "bg-white cursor-pointer hover:bg-gray-50"
          }`}
          disabled={!assemblyName || (dropdownDisabled && !isViewOnlySaved)}
          title={projectNames.length > 1 ? projectNames.join(", ") : (isViewOnlySaved ? "Click to view saved projects" : undefined)}
        >
          <span className="whitespace-normal break-words leading-tight">
            {projectNames.length === 0
              ? "Select project(s)"
              : projectNames.length === 1
              ? formatProjectNameForWrap(projectNames[0])
              : `Multiple (${projectDetails.length})`}
          </span>
          <ExpandMoreIcon className="text-gray-600 flex-shrink-0 ml-1" />
        </button>

        {isOpen && assemblyName && (canOpenDropdown && (!dropdownDisabled || isViewOnlySaved)) && (
          <div
            ref={dropdownRef}
            className="fixed bg-white border border-gray-300 rounded-md shadow-lg z-[99999] overflow-y-auto"
            style={{ 
              top: `${dropdownPosition.top}px`,
              left: `${dropdownPosition.left}px`,
              width: `${Math.max(dropdownPosition.width, 300)}px`,
              minWidth: "300px",
              maxHeight: `${dropdownPosition.maxHeight ?? 240}px`
            }}
          >
            {/* Selected Projects Section */}
            {projectDetails.length > 0 && (
              <div className="p-2 border-b border-gray-200">
                <div className="flex items-center justify-between mb-1">
                  <div className="text-xs font-semibold text-gray-700">
                    {isViewOnlySaved ? "Saved Projects" : "Selected Projects"} ({projectDetails.length}):
                  </div>
                  <div className="text-xs text-gray-600">
                    Total: {totalBlockedUnits} units
                  </div>
                </div>
                {projectDetails
                  .filter(p => {
                    const hasValidId = !!(extractProjectIdFromDetail(p) || p.projectId || p._id);
                    const hasValidQuantity = (Number(p.quantity) || 0) > 0;
                    return hasValidId && hasValidQuantity;
                  })
                  .map((project, idx) => (
                  <div
                    key={extractProjectIdFromDetail(project) || project.projectId || project._id || idx}
                    className={`flex items-center justify-between p-1.5 mb-1 rounded ${isViewOnlySaved ? "bg-gray-50" : "bg-blue-50 hover:bg-blue-100"}`}
                  >
                    <span className="text-xs text-gray-700 flex-1">
                      {formatProjectNameForWrap(getDetailDisplayName(project))}{" "}
                      - {project.quantity} units
                    </span>
                    {!isViewOnlySaved && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          onProjectRemove(project.projectId || project._id);
                        }}
                        className="ml-2 p-0.5 hover:bg-red-200 rounded text-red-600"
                        title="Remove project"
                      >
                        <X size={14} />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Available Projects Section - only when editing (not view-only saved) */}
            {!isViewOnlySaved && (
            <div className="p-2">
              <div className="flex items-center justify-between mb-1">
                <div className="text-xs font-semibold text-gray-700">
                  Available Projects:
                </div>
                {unselectedProjects.length > 0 && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      // Select all available projects at once
                      unselectedProjects.forEach((project) => {
                        onProjectSelect(project);
                      });
                    }}
                    className="text-xs text-blue-600 hover:text-blue-800 underline"
                  >
                    Select All
                  </button>
                )}
              </div>
              <div className="max-h-48 overflow-y-auto overflow-x-hidden overscroll-contain min-h-0">
                {unselectedProjects.length > 0 ? (
                  unselectedProjects.map((project, idx) => (
                    <div
                      key={project.projectId || project._id || idx}
                      onClick={(e) => {
                        e.stopPropagation();
                        onProjectSelect(project);
                      }}
                      onMouseDown={(e) => e.stopPropagation()}
                      className="p-1.5 mb-1 hover:bg-gray-100 rounded cursor-pointer text-xs text-gray-700 flex items-center"
                    >
                      <span className="flex-1 whitespace-normal break-words">
                        {formatProjectNameForWrap(
                          project.projectName ||
                            project.name ||
                            project.ProjectName ||
                            `Project (${project.projectId || project._id})`
                        )}{" "}
                        - {project.quantity} units
                      </span>
                      <span className="text-blue-600 ml-2">+ Add</span>
                    </div>
                  ))
                ) : (
                  <div className="text-xs text-gray-500 p-2">
                    {availableProjects.length === 0
                      ? "No projects found for this assembly"
                      : "All available projects are selected"}
                  </div>
                )}
              </div>
            </div>
            )}
            
            {/* Close button at bottom - only when editing (not in saved projects view) */}
            {!isViewOnlySaved && (
            <div className="p-2 border-t border-gray-200 flex justify-end">
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setIsOpen(false);
                }}
                className="text-xs bg-blue-600 text-white px-3 py-1.5 rounded hover:bg-blue-700"
              >
                Done
              </button>
            </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const Inventory = () => {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const [isLoading, SetisLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const { items } = useSelector((state) => state.inventory);
  const [id, setRowToDelete] = useState(null);
  const [optimisticallyDeletedIds, setOptimisticallyDeletedIds] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isModal, setIsModal] = useState(false);
  const [deleteIndex, setDeleteIndex] = useState(null);
  const loggedUser = useLoggedUser();
  const hasNoFeatureConfig = !loggedUser?.featureAccess || loggedUser.featureAccess.length === 0;
  const inventoryAddLevel = getFeatureAccessLevel(loggedUser, "INVENTORY ADD INVENTORY");
  const inventoryEditLevel = getFeatureAccessLevel(loggedUser, "INVENTORY EDIT INVENTORY");
  const addNorm = inventoryAddLevel ? String(inventoryAddLevel).trim().toUpperCase() : "";
  const editNorm = inventoryEditLevel ? String(inventoryEditLevel).trim().toUpperCase() : "";
  const isViewOnly =
    !hasNoFeatureConfig &&
    (addNorm === "VIEW ONLY" || editNorm === "VIEW ONLY");
  const isAddInventoryNoAccess =
    !hasNoFeatureConfig && !hasViewOrFullAccess(loggedUser, "INVENTORY ADD INVENTORY");
  const isInventoryInteractionDisabled = isViewOnly || isAddInventoryNoAccess;
  const { inventory } = useSelector((state) => state.assemblyInventory);
  // const { data } = useSelector(state => state.partsInventory);
  const hardwareData = useSelector((state) => state.hardware.data);
  // const [addedrow, setaddedrow] = useState(0);
  const SidebarInventoryGroup = useSelector(
    (state) => state.sidebar.SidebarInventoryGroup
  );
  const groupsFromSlice = useSelector((state) => state.group?.group || []);
  const { projects } = useSelector((state) => state.projects);
  const [, setongoingProjects] = useState([]);
  const { hardwareProjectTracking, items: hardwareDescriptionItems } = useSelector(
    (state) => state.hardwareDescription
  );
  // Add this after your other useState declarations:
  const generateRowId = useCallback(() => {
    return `temp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }, []);
  const selectedInventoryGroupName = useMemo(() => {
    const selectedId = String(SidebarInventoryGroup || "").trim();
    if (!selectedId) return "";
    const list = Array.isArray(groupsFromSlice) ? groupsFromSlice : [];
    const matchedGroup = list.find((groupItem) => {
      const id = String(groupItem?._id ?? groupItem?.id ?? "").trim();
      return id === selectedId;
    });
    return String(
      matchedGroup?.name ??
      matchedGroup?.newGroupName ??
      matchedGroup?.groupName ??
      selectedId
    ).trim();
  }, [SidebarInventoryGroup, groupsFromSlice]);
  const isMatchingSelectedGroup = useCallback(
    (groupValue) => {
      if (!SidebarInventoryGroup) return true;
      const normalizedGroupValue = String(groupValue ?? "").trim();
      return (
        normalizedGroupValue === String(SidebarInventoryGroup).trim() ||
        normalizedGroupValue === selectedInventoryGroupName
      );
    },
    [SidebarInventoryGroup, selectedInventoryGroupName]
  );
  /** MongoDB ObjectId for POST /inventorydata (sidebar selection is already the id). */
  const resolveInventorySubmitGroupId = useCallback(
    (row) => {
      const fromSidebar = String(SidebarInventoryGroup || "").trim();
      if (fromSidebar) return fromSidebar;
      const fromRow = String(row?.groupid ?? "").trim();
      if (/^[a-f0-9]{24}$/i.test(fromRow)) return fromRow;
      const gName = String(row?.group || "").trim();
      if (!gName) return "";
      const list = Array.isArray(groupsFromSlice) ? groupsFromSlice : [];
      const matched = list.find((g) => {
        const n = String(
          g?.name ?? g?.newGroupName ?? g?.groupName ?? ""
        ).trim();
        return (
          n &&
          (n === gName || n.toLowerCase() === gName.toLowerCase())
        );
      });
      return String(matched?._id ?? matched?.id ?? "").trim();
    },
    [SidebarInventoryGroup, groupsFromSlice]
  );
  console.log(
    "hardwareProjectTracking hardwareProjectTracking",
    hardwareProjectTracking
  );
  console.log("hardwareDatahardwareData", hardwareData);
  console.log("=== fetchHardwareDescriptions RECEIVED DATA ===", {
    hardwareDescriptionItems,
    itemsLength: hardwareDescriptionItems?.length || 0,
    sampleItem: hardwareDescriptionItems?.[0],
    allItems: hardwareDescriptionItems,
  });

  useEffect(() => {
    dispatch(setCurrentPageTitle("Inventory"));
  }, [dispatch]);

  // Get unique assembly names from master inventory (hardware manager settings/masters) for dropdown
  const [assemblyOptions, setAssemblyOptions] = useState([]);
  
  useEffect(() => {
    console.log("Setting assembly options from master inventory", {
      inventoryLength: inventory?.length || 0,
      SidebarInventoryGroup,
      inventory: inventory,
    });
    
    // Handle different response structures (array or object with data property)
    let inventoryArray = inventory;
    if (inventory && typeof inventory === 'object' && !Array.isArray(inventory)) {
      if (inventory.data && Array.isArray(inventory.data)) {
        inventoryArray = inventory.data;
      } else if (inventory.inventory && Array.isArray(inventory.inventory)) {
        inventoryArray = inventory.inventory;
      }
    }
    
    if (inventoryArray && Array.isArray(inventoryArray) && inventoryArray.length > 0) {
      // Filter by SidebarInventoryGroup to get assemblies for current group
      const filteredByGroup = SidebarInventoryGroup
        ? inventoryArray.filter(
            (item) =>
              item &&
              isMatchingSelectedGroup(item.groupid ?? item.group)
          )
        : inventoryArray;
      
      console.log("Filtered by group:", {
        filteredCount: filteredByGroup.length,
        sampleItem: filteredByGroup[0],
        allGroups: [
          ...new Set(
            inventoryArray
              .map((i) => i?.groupid ?? i?.group)
              .filter(Boolean)
          ),
        ],
      });
      
      // Get unique assembly names from master inventory
      const uniqueAssemblies = [...new Set(
        filteredByGroup
          .map((item) => item?.assembly)
          .filter((assembly) => assembly && assembly.trim() !== "")
      )].sort();

      console.log("Unique assemblies for dropdown:", uniqueAssemblies);
      setAssemblyOptions(uniqueAssemblies);
    } else {
      console.log("No inventory data available", {
        inventory,
        inventoryArray,
        isArray: Array.isArray(inventoryArray),
        length: inventoryArray?.length,
      });
      setAssemblyOptions([]);
    }
  }, [inventory, SidebarInventoryGroup, isMatchingSelectedGroup]);
  useEffect(() => {
    dispatch(getInventoryByCreatedBy());
    dispatch(getAllAssemblyInventory());
    dispatch(fetchHardware());
    dispatch(fetchAllProjects());
    dispatch(fetchHardwareDescriptions());
    dispatch(fetchEmployees());
  }, [dispatch]);
  const refresh = () => {
    dispatch(getInventoryByCreatedBy());
    dispatch(getAllAssemblyInventory());
    dispatch(fetchHardware());
    dispatch(fetchAllProjects());
    dispatch(fetchHardwareDescriptions());
  };
  console.log("hardwareData hardwareData", hardwareData);
  // Initial state with one row - matching the model structure
  const defaultInventoryRow = useCallback(
    () => ({
      _id: generateRowId(),
      assembly: "",
      unitsAvailable: "",
      unitsRequired: "",
      actualUnitsRequired: "",
      minimumQuantity: "",
      group: selectedInventoryGroupName || SidebarInventoryGroup,
      projectDetails: [],
      inventoryDetails: [
        {
          _id: generateRowId(),
          subparts: "",
          unitsAvailableDetails: "",
          unitsRequiredDetails: "",
          unitsPerAssemblyDetails: "",
          unitsMinimumQuantityDetails: "",
          supplier: "",
          orderingStatus: "",
          threshold: "",
        },
      ],
    }),
    [SidebarInventoryGroup, selectedInventoryGroupName, generateRowId]
  );

  // Replace the initial state:
  const [inventoryRows, setInventoryRows] = useState(() => [
    defaultInventoryRow(),
  ]);
  console.log("inventoryRows", inventoryRows);

  // Keep saved DB entries hidden in the UI (only show temp rows while editing).
  // This fixes "Inventory shows saved database entries by default" issue.
  const [showSavedInventoryEntries, setShowSavedInventoryEntries] = useState(false);
  const savedEntriesPreviewTimeoutRef = React.useRef(null);
  const recentlySubmittedAssembliesRef = React.useRef([]);

  const triggerSavedEntriesPreview = useCallback((submittedRows = []) => {
    recentlySubmittedAssembliesRef.current = (submittedRows || [])
      .map((row) => String(row?.assembly || "").trim().toLowerCase())
      .filter(Boolean);
    if (savedEntriesPreviewTimeoutRef.current) {
      clearTimeout(savedEntriesPreviewTimeoutRef.current);
    }
    setShowSavedInventoryEntries(true);
    savedEntriesPreviewTimeoutRef.current = setTimeout(() => {
      setShowSavedInventoryEntries(false);
      recentlySubmittedAssembliesRef.current = [];
      savedEntriesPreviewTimeoutRef.current = null;
    }, 2000);
  }, []);

  useEffect(() => {
    return () => {
      if (savedEntriesPreviewTimeoutRef.current) {
        clearTimeout(savedEntriesPreviewTimeoutRef.current);
      }
      recentlySubmittedAssembliesRef.current = [];
    };
  }, []);

  // Reset local table state whenever inventory group changes
  // so rows from previously selected group are not carried over.
  useEffect(() => {
    setInventoryRows([defaultInventoryRow()]);
    setCurrentPage(1);
    setOptimisticallyDeletedIds([]);
  }, [SidebarInventoryGroup, defaultInventoryRow]);


  const LoggedUser = localStorage.getItem("email");
  const isOptimisticallyDeleted = useCallback(
    (rowId) => {
      if (!rowId) return false;
      return optimisticallyDeletedIds.includes(String(rowId));
    },
    [optimisticallyDeletedIds]
  );

  // Calculate blocked units from projectDetails
  const calculateBlockedUnits = useCallback((projectDetails) => {
    if (!projectDetails || !Array.isArray(projectDetails)) return 0;
    return projectDetails.reduce((sum, project) => {
      return sum + (Number(project.quantity) || 0);
    }, 0);
  }, []);

  // Calculate units available considering blocked units
  const calculateUnitsAvailable = useCallback((originalUnitsAvailable, projectDetails) => {
    const original = Number(originalUnitsAvailable) || 0;
    const blocked = calculateBlockedUnits(projectDetails);
    return Math.max(0, original - blocked); // Don't allow negative
  }, [calculateBlockedUnits]);

  const getActualUnitRequiredValue = useCallback((data = {}) => {
    return Number(
      data.actualunitrequired ??
        data.actualUnitRequired ??
        data.actualUnitsRequired ??
        data.quantity ??
        0
    ) || 0;
  }, []);

  const getTotalUnitRequiredValue = useCallback((data = {}) => {
    const totalFromPayload = Number(
      data.totalUnitsrequired ??
        data.totalunitsrequired ??
        data.totalUnitsRequired
    );
    if (!Number.isNaN(totalFromPayload) && totalFromPayload > 0) {
      return totalFromPayload;
    }
    return getActualUnitRequiredValue(data);
  }, [getActualUnitRequiredValue]);

  // Get available projects for a specific assembly from hardwareDescriptionItems
  // Data comes from "Project request outstanding bucket" where managers submit required assembly and quantity
  // Projects are identified using projectid only (not quotationId)
  const getAvailableProjectsForAssembly = useCallback((assemblyName) => {
    if (!assemblyName || !hardwareDescriptionItems || !projects) {
      return [];
    }

    if (hardwareDescriptionItems.length === 0) {
      return [];
    }

    // All hardwareDescription entries for this assembly (no status filter) — quantity = highest for project+assembly
    const allEntriesForAssembly = hardwareDescriptionItems.filter((entry) => {
      if (!entry.assemblepartdescription) return false;
      return entry.assemblepartdescription.trim().toLowerCase() === assemblyName.trim().toLowerCase();
    });

    if (allEntriesForAssembly.length === 0) {
      return [];
    }

    // Get unique project identifiers from all entries for this assembly
    const collectedIds = new Set();
    allEntriesForAssembly.forEach((e) => {
      const pid = e.projectid || e.projectId;
      const qid = e.quotationId || e.quotationid;
      if (pid) collectedIds.add(pid);
      if (qid) collectedIds.add(qid);
    });
    const projectIds = [...collectedIds];

    const projectMap = new Map();

    projectIds.forEach((id) => {
      const idStr = id != null ? String(id) : "";
      if (!idStr) return;
      // Find project by _id first, then by quotationId (one project can have multiple assemblies under same quotationId)
      let project = projects.find((p) =>
        p._id != null && (p._id === id || String(p._id) === idStr)
      );
      if (!project) {
        project = projects.find((p) =>
          (p.quotationId != null && (p.quotationId === id || String(p.quotationId) === idStr)) ||
          (p.QuotationId != null && (p.QuotationId === id || String(p.QuotationId) === idStr))
        );
      }

      if (project) {
        // Optional: Filter by group if SidebarInventoryGroup is set
        if (
          SidebarInventoryGroup &&
          (project.groupid || project.AssignedGroup) &&
          !isMatchingSelectedGroup(project.groupid ?? project.AssignedGroup)
        ) {
          return; // Skip projects from different groups
        }

        // Check if project is in "project request" status with taskStatus "submit"
        // This means managers/resources have submitted responses
        const hasSubmittedResponse = project.Assignedperson && 
          Array.isArray(project.Assignedperson) &&
          project.Assignedperson.some((person) => {
            const taskStatus = person.taskStatus || person.taskstatus || "";
            const managerStatus = person.managerstatus || person.managerStatus || "";
            
            // Check for "project request" managerStatus and "submit" taskStatus (both must be true)
            return (
              (managerStatus === "project request" ||
               managerStatus === "Project Request" ||
               managerStatus === "project request outstanding")
            ) && (
              taskStatus === "submit" ||
              taskStatus === "Submit"
            );
          });

        // Only include projects that meet the status criteria
        if (!hasSubmittedResponse) {
          return; // Skip projects that don't meet the criteria
        }

        // Entries for this project + selected assembly: match by project._id / quotationId
        const projectQuotationId = project.quotationId ?? project.QuotationId;
        const projectEntries = allEntriesForAssembly.filter((e) => {
          const entryProjectId = e.projectid || e.projectId;
          const entryQuotationId = e.quotationId || e.quotationid;
          const matchId = idStr && (
            (entryProjectId && (entryProjectId === id || String(entryProjectId) === idStr)) ||
            (entryQuotationId && (entryQuotationId === id || String(entryQuotationId) === idStr))
          );
          const matchProject = (entryProjectId && project._id && (String(entryProjectId) === String(project._id))) ||
            (entryQuotationId && projectQuotationId != null && (String(entryQuotationId) === String(projectQuotationId)));
          return matchId || matchProject;
        });

        // For each project we keep:
        // - quantity => actualunitrequired (used when selecting projects)
        // - totalUnitsRequired => totalUnitsrequired (used for display before selection)
        const totalQuantity = projectEntries.length > 0
          ? Math.max(...projectEntries.map((entry) => getActualUnitRequiredValue(entry)))
          : 0;
        const totalUnitsRequired = projectEntries.length > 0
          ? Math.max(...projectEntries.map((entry) => getTotalUnitRequiredValue(entry)))
          : 0;

        if (totalQuantity > 0) {
          const key = project._id;
          const quotationId = project.quotationId ?? project.QuotationId;
          projectMap.set(key, {
            projectId: project._id,
            quotationId: quotationId != null ? quotationId : undefined,
            projectName: project.ProjectName || project.projectName || project.name || "Unknown Project",
            quantity: totalQuantity,
            totalUnitsRequired,
          });
        }
      }
    });

    return Array.from(projectMap.values());
  }, [hardwareDescriptionItems, projects, SidebarInventoryGroup, getActualUnitRequiredValue, getTotalUnitRequiredValue, isMatchingSelectedGroup]);

  // Project IDs (and quotationIds) already saved for an assembly: do not show them again in Block Units "Available"
  // (e.g. if assembly ABC is already added for project RT, selecting ABC again should not show RT)
  // Hierarchy: group -> assembly -> project (use project.id / quotationId for unique project)
  const getAlreadySavedProjectIdsForAssembly = useCallback((assemblyName) => {
    if (!assemblyName || !items || !Array.isArray(items)) return new Set();
    const set = new Set();
    const assemblyLower = assemblyName.trim().toLowerCase();
    items.forEach((item) => {
      if (isOptimisticallyDeleted(item?._id)) return;
      if (!item || item.createdBy !== loggedUser?._id) return;
      if (SidebarInventoryGroup && !isMatchingSelectedGroup(item.groupid ?? item.group)) return;
      const itemAssembly = (item.assembly || "").trim().toLowerCase();
      if (itemAssembly !== assemblyLower) return;
      const details = item.projectDetails || [];
      details.forEach((pd) => {
        // Support both UI shape and backend payload shape
        const pid = pd.projectId || pd.projectid || pd._id;
        const qid = pd.quotationId || pd.quotationid;
        if (pid != null) set.add(String(pid));
        if (qid != null) set.add(String(qid));
      });
    });
    return set;
  }, [items, loggedUser?._id, SidebarInventoryGroup, isOptimisticallyDeleted, isMatchingSelectedGroup]);

  // Project IDs already used for this assembly in OTHER rows (same group, same assembly). Exclude current row.
  // So when adding same assembly again, block-units dropdown does not show projects already used for that assembly in other rows.
  const getAlreadyUsedProjectIdsInOtherRows = useCallback((assemblyName, excludeRowId) => {
    if (!assemblyName || !inventoryRows || !Array.isArray(inventoryRows)) return new Set();
    const set = new Set();
    const assemblyLower = assemblyName.trim().toLowerCase();
    inventoryRows.forEach((r) => {
      if (!r || r._id === excludeRowId) return;
      if (SidebarInventoryGroup && !isMatchingSelectedGroup(r.groupid ?? r.group)) return;
      const rowAssembly = (r.assembly || "").trim().toLowerCase();
      if (rowAssembly !== assemblyLower) return;
      const details = r.projectDetails || [];
      details.forEach((pd) => {
        // Support both UI shape and backend payload shape
        const pid = pd.projectId || pd.projectid || pd._id;
        const qid = pd.quotationId || pd.quotationid;
        if (pid != null) set.add(String(pid));
        if (qid != null) set.add(String(qid));
      });
    });
    return set;
  }, [inventoryRows, SidebarInventoryGroup, isMatchingSelectedGroup]);

  useEffect(() => {
    const filtered = projects.filter(
      (proj) =>
        Array.isArray(proj.Assignedperson) &&
        proj.Assignedperson.some(
          (person) => person.managerstatus === "project ongoing"
        )
    );
    const managerProjects = filtered.filter((proj) =>
      proj.Assignedperson?.some(
        (person) => person.email?.toLowerCase() === LoggedUser.toLowerCase()
      )
    );
    setongoingProjects(managerProjects);
  }, [projects, LoggedUser]);

  // Get unitsAvailable and minimumQuantity from master assembly inventory only (current DB value).
  // Use this when user selects an assembly so UNITS AVAILABLE shows updated value from database (e.g. 320), not a previously saved value (e.g. 330).
  const getMasterUnitsAvailableForAssembly = useCallback((assemblyName) => {
    if (!assemblyName) {
      return { unitsAvailable: "", minimumQuantity: "" };
    }
    if (!inventory) {
      return { unitsAvailable: "", minimumQuantity: "" };
    }
    let inventoryArray = inventory;
    if (typeof inventory === "object" && !Array.isArray(inventory)) {
      if (inventory.data && Array.isArray(inventory.data)) inventoryArray = inventory.data;
      else if (inventory.inventory && Array.isArray(inventory.inventory)) inventoryArray = inventory.inventory;
    }
    if (!inventoryArray || !Array.isArray(inventoryArray) || inventoryArray.length === 0) {
      return { unitsAvailable: "", minimumQuantity: "" };
    }
    const filteredByGroup = SidebarInventoryGroup
      ? inventoryArray.filter((item) => item && isMatchingSelectedGroup(item.groupid ?? item.group))
      : inventoryArray;
    const matchingAssembly = filteredByGroup.find(
      (item) =>
        item.assembly &&
        item.assembly.trim().toLowerCase() === assemblyName.trim().toLowerCase()
    );
    if (!matchingAssembly) {
      return { unitsAvailable: "", minimumQuantity: "" };
    }
    return {
      unitsAvailable: matchingAssembly.unitsAvailable != null && String(matchingAssembly.unitsAvailable).trim() !== ""
        ? matchingAssembly.unitsAvailable.toString()
        : "0",
      minimumQuantity: matchingAssembly.minimumquantity != null && String(matchingAssembly.minimumquantity).trim() !== ""
        ? matchingAssembly.minimumquantity.toString()
        : "",
    };
  }, [inventory, SidebarInventoryGroup, isMatchingSelectedGroup]);

  // Get unitsAvailable and minimumQuantity: prefer saved inventory row (items) for display of saved data only; else master (assembly inventory).
  // For showing saved data use inventory (items).unitsAvailable; when selecting assembly use getMasterUnitsAvailableForAssembly for current DB value.
  const getUnitsAvailableForAssembly = useCallback((assemblyName) => {
    if (!assemblyName) {
      return { unitsAvailable: "", minimumQuantity: "" };
    }

    // For saved rows: use saved inventory (items) unitsAvailable so displayed saved data matches DB.
    // Always use minimumQuantity from master assembly inventory so Inventory section shows assembly's min quantity (not saved 0).
    if (items && Array.isArray(items) && loggedUser?._id && SidebarInventoryGroup) {
      const savedRow = items.find(
        (item) =>
          !isOptimisticallyDeleted(item?._id) &&
          item.createdBy === loggedUser._id &&
          isMatchingSelectedGroup(item.groupid ?? item.group) &&
          item.assembly &&
          item.assembly.trim().toLowerCase() === assemblyName.trim().toLowerCase()
      );
      if (savedRow && savedRow.unitsAvailable != null && String(savedRow.unitsAvailable).trim() !== "") {
        const unitsAvailable = String(savedRow.unitsAvailable).trim();
        // Resolve minimumQuantity from master assembly inventory (so we show 10, not saved 0)
        const { minimumQuantity: masterMin } = getMasterUnitsAvailableForAssembly(assemblyName);
        return { unitsAvailable, minimumQuantity: masterMin || "" };
      }
    }

    // Fallback: master assembly inventory (current DB value)
    return getMasterUnitsAvailableForAssembly(assemblyName);
  }, [SidebarInventoryGroup, items, loggedUser, getMasterUnitsAvailableForAssembly, isOptimisticallyDeleted, isMatchingSelectedGroup]);

  // Replace your useEffect with this:
  useEffect(() => {
    if (!showSavedInventoryEntries) {
      // Requirement: do not show saved database entries.
      // Keep only locally created rows (temp rows) in the UI.
      setInventoryRows((prev) => {
        const localOnlyRows = (prev || []).filter(
          (r) => r?._id && String(r._id).includes("temp")
        );
        if (localOnlyRows.length > 0) return prev;
        return [defaultInventoryRow()];
      });
    } else if (items && items.length > 0) {
      const filteredItems = items.filter(
        (item) =>
          item.createdBy === loggedUser?._id &&
          !isOptimisticallyDeleted(item?._id)
      );
      const filteredData = filteredItems.filter(
        (item) => isMatchingSelectedGroup(item.groupid ?? item.group)
      );
      const recentlySubmittedAssemblies = recentlySubmittedAssembliesRef.current || [];
      const previewData =
        recentlySubmittedAssemblies.length > 0
          ? filteredData.filter((item) =>
              recentlySubmittedAssemblies.includes(
                String(item?.assembly || "").trim().toLowerCase()
              )
            )
          : filteredData;

      if (previewData.length > 0) {
        const mapped = previewData.map((item) => {
          // IMPORTANT: Preserve projectDetails from saved data
          let projectDetails = item.projectDetails || [];
          
          // Ensure projectDetails is an array
          if (!Array.isArray(projectDetails)) {
            projectDetails = [];
          }
          
          console.log("Loading item from backend:", {
            _id: item._id,
            assembly: item.assembly,
            projectDetailsFromBackend: item.projectDetails,
            projectDetailsLength: projectDetails.length,
          });
          
          // Ensure projectDetails have projectName - resolve from availableProjects or global projects
          // Backend may return projectDetails with only _id (MongoDB ObjectId) - normalize to quotationId/projectId
          if (projectDetails.length > 0 && item.assembly) {
            const availableProjects = getAvailableProjectsForAssembly(item.assembly);
            const resolveProjectName = (projectId) => {
              if (!projectId) return "";
              const idStr = String(projectId);
              const fromAvailable = availableProjects.find(
                (ap) =>
                  String(ap.projectId || ap._id) === idStr ||
                  (ap.quotationId != null && String(ap.quotationId) === idStr)
              );
              if (fromAvailable && (fromAvailable.projectName || fromAvailable.ProjectName)) {
                return fromAvailable.projectName || fromAvailable.ProjectName;
              }
              return resolveProjectDisplayName(projectId, projects, []);
            };
            // Backend returns projectid (lowercase) and units; normalize to our shape and resolve projectName from projectid
            projectDetails = projectDetails
              .map((pd) => {
                const id = extractProjectIdFromDetail(pd);
                const idStr = id != null ? String(id) : "";
                const qty = Number(pd.units ?? pd.quantity) || 0;
                const populatedName =
                  typeof pd.projectid === "object" && pd.projectid != null
                    ? pd.projectid.ProjectName || pd.projectid.projectName || pd.projectid.name
                    : "";
                const resolvedName = resolveProjectName(id);
                // Use manager-only quantity for display when available (match by projectId or quotationId - one project can have multiple assemblies)
                const ap = availableProjects.find((a) =>
                  String(a.projectId || a._id) === idStr ||
                  (a.quotationId != null && String(a.quotationId) === idStr)
                );
                const displayQuantity = ap != null ? ap.quantity : qty;
                const projectName = resolveProjectDisplayName(id, projects, [
                  populatedName,
                  pd.projectName,
                  pd.name,
                  pd.ProjectName,
                  resolvedName,
                ]);
                return {
                  _id: pd._id,
                  projectId: id,
                  projectName,
                  quantity: displayQuantity,
                };
              })
              .filter((pd) => !!(pd.projectId || pd._id) && pd.quantity > 0);
          }
          
          // Get unitsAvailable from master inventory (for fallback when no saved value)
          const { unitsAvailable: masterUnitsAvailable, minimumQuantity: masterMinimumQuantity } = 
            getUnitsAvailableForAssembly(item.assembly);
          
          // Use saved unitsAvailable from API when present (so after save we show 34, not master minus required)
          const originalUnitsAvailable = (item.unitsAvailable != null && String(item.unitsAvailable).trim() !== "")
            ? String(item.unitsAvailable).trim()
            : (masterUnitsAvailable || "0");
          // Use saved minimumQuantity from API when present so Inventory section shows DB value (e.g. 10); else fallback to master
          const savedMinQty = (item.minimumQuantity != null && String(item.minimumQuantity).trim() !== "")
            ? String(item.minimumQuantity).trim()
            : ((item.minimumquantity != null && String(item.minimumquantity).trim() !== "")
              ? String(item.minimumquantity).trim()
              : "");
          const minimumQuantity = savedMinQty || masterMinimumQuantity || "";
          
          // Calculate actual unitsRequired from selected projectDetails
          const calculatedUnitsRequired = projectDetails.reduce(
            (sum, project) => sum + (Number(project.quantity) || 0),
            0
          );
          // Display column should prefer totalUnitsrequired from backend when present
          const totalUnitsRequiredFromBackend =
            item.totalUnitsrequired != null && String(item.totalUnitsrequired).trim() !== "";
          const unitsRequiredFromBackend = item.unitsRequired != null && String(item.unitsRequired).trim() !== "";
          const unitsRequiredValue = unitsRequiredFromBackend
            ? String(item.unitsRequired).trim()
            : calculatedUnitsRequired.toString();
          const totalUnitsRequiredValue = totalUnitsRequiredFromBackend
            ? String(item.totalUnitsrequired).trim()
            : unitsRequiredValue;
          
          console.log("Loading row with projectDetails:", {
            assembly: item.assembly,
            projectDetailsCount: projectDetails.length,
            projectDetails: projectDetails,
            calculatedUnitsRequired: calculatedUnitsRequired,
            savedUnitsRequired: item.unitsRequired,
            unitsRequiredDisplay: unitsRequiredValue,
            itemProjectDetails: item.projectDetails,
          });
          
          // CRITICAL: Ensure projectDetails are preserved from backend (e.g. when only _id is present)
          if (projectDetails.length === 0 && item.projectDetails && Array.isArray(item.projectDetails) && item.projectDetails.length > 0) {
            console.log("projectDetails was empty after map, using raw item.projectDetails from backend:", item.projectDetails);
            const availableProjectsFallback = getAvailableProjectsForAssembly(item.assembly);
            const resolveName = (id) => {
              if (!id) return "";
              const idStr = String(id);
              const ap = availableProjectsFallback.find(
                (a) =>
                  String(a.projectId || a._id) === idStr ||
                  (a.quotationId != null && String(a.quotationId) === idStr)
              );
              if (ap && (ap.projectName || ap.ProjectName)) return ap.projectName || ap.ProjectName;
              return resolveProjectDisplayName(id, projects, []);
            };
            projectDetails = item.projectDetails.map((pd) => {
              const id = extractProjectIdFromDetail(pd);
              const idStr = id != null ? String(id) : "";
              const populatedName =
                typeof pd.projectid === "object" && pd.projectid != null
                  ? pd.projectid.ProjectName || pd.projectid.projectName || pd.projectid.name
                  : "";
              const name = resolveProjectDisplayName(id, projects, [
                populatedName,
                pd.projectName,
                pd.name,
                pd.ProjectName,
                resolveName(id),
              ]);
              const qty = Number(pd.units ?? pd.quantity) || 0;
              const ap = availableProjectsFallback.find((a) =>
                String(a.projectId || a._id) === idStr ||
                (a.quotationId != null && String(a.quotationId) === idStr)
              );
              const displayQuantity = ap != null ? ap.quantity : qty;
              return {
                _id: pd._id,
                projectId: id,
                projectName: name,
                quantity: displayQuantity,
              };
            }).filter((pd) => (pd.projectId || pd._id) && pd.quantity > 0);
            const recalc = projectDetails.reduce((s, p) => s + (Number(p.quantity) || 0), 0);
            return {
              _id: item._id || generateRowId(),
              assembly: item.assembly || "",
              unitsAvailable: originalUnitsAvailable.toString(),
              unitsRequired: totalUnitsRequiredValue,
              actualUnitsRequired: recalc.toString(),
              minimumQuantity: minimumQuantity,
              group: item.group || "",
              projectDetails: projectDetails,
              inventoryDetails: (item.inventoryDetails || []).map((p) => ({
                _id: p._id || generateRowId(),
                subparts: p.subparts || "",
                unitsAvailableDetails: p.unitsAvailableDetails || "",
                unitsRequiredDetails: p.unitsRequiredDetails || "",
                unitsPerAssemblyDetails: p.unitsPerAssemblyDetails || "",
                unitsMinimumQuantityDetails: p.unitsMinimumQuantityDetails || "",
                supplier: p.supplier || "",
                orderingStatus: p.orderingStatus || "",
                threshold: p.threshold || "",
              })),
            };
          }
          
          return {
            _id: item._id || generateRowId(),
            assembly: item.assembly || "",
            unitsAvailable: originalUnitsAvailable.toString(),
            unitsRequired: totalUnitsRequiredValue,
            actualUnitsRequired: calculatedUnitsRequired.toString(),
            minimumQuantity: minimumQuantity,
            group: item.group || "",
            projectDetails: projectDetails,
            inventoryDetails: (item.inventoryDetails || []).map((p) => ({
            _id: p._id || generateRowId(),
            subparts: p.subparts || "",
            unitsAvailableDetails: p.unitsAvailableDetails || "",
            unitsRequiredDetails: p.unitsRequiredDetails || "",
            unitsPerAssemblyDetails: p.unitsPerAssemblyDetails || "",
            unitsMinimumQuantityDetails: p.unitsMinimumQuantityDetails || "",
            supplier: p.supplier || "",
            orderingStatus: p.orderingStatus || "",
            threshold: p.threshold || "",
          })),
          };
        });

        // Always use master inventory values - don't recalculate during editing
        // Preserve locally added rows (temp id) only when user added rows after load, so "Add Assembly" is not overwritten
        setInventoryRows((prev) => {
          const localOnlyRows = (prev || []).filter(
            (r) => r._id && String(r._id).includes("temp")
          );
          const hasUserAdded = (prev?.length || 0) > mapped.length && localOnlyRows.length > 0;
          // Preserve locally added subpart rows (temp _id) so "Add Subpart" rows do not disappear when sync runs
          const merged = mapped.map((mRow) => {
            const prevRow = (prev || []).find((r) => r._id === mRow._id);
            if (!prevRow || !Array.isArray(prevRow.inventoryDetails)) return mRow;
            const savedDetails = mRow.inventoryDetails || [];
            const localOnlyDetails = (prevRow.inventoryDetails || []).filter(
              (p) => p && p._id && String(p._id).includes("temp")
            );
            if (localOnlyDetails.length === 0) return mRow;
            return { ...mRow, inventoryDetails: [...savedDetails, ...localOnlyDetails] };
          });
          if (!hasUserAdded) return merged;
          return [...merged, ...localOnlyRows];
        });
      } else {
        // Only set default row if there are truly no rows (preserve local added rows)
        setInventoryRows((prev) => {
          const localOnlyRows = (prev || []).filter(
            (r) => r._id && String(r._id).includes("temp")
          );
          if (localOnlyRows.length > 0) return prev;
          return [defaultInventoryRow()];
        });
      }
    } else {
      // Handle case when items is null or empty (preserve local added rows)
      setInventoryRows((prev) => {
        const localOnlyRows = (prev || []).filter(
          (r) => r._id && String(r._id).includes("temp")
        );
        if (localOnlyRows.length > 0) return prev;
        return [defaultInventoryRow()];
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally omit inventoryRows: run only when API/data deps change, not on every row edit
  }, [
    showSavedInventoryEntries,
    items,
    loggedUser,
    SidebarInventoryGroup,
    generateRowId,
    defaultInventoryRow,
    calculateBlockedUnits,
    calculateUnitsAvailable,
    inventory,
    getUnitsAvailableForAssembly,
    getAvailableProjectsForAssembly,
    projects,
    isOptimisticallyDeleted,
  ]);

  // Calculate pagination data
  const totalItems = inventoryRows?.length;
  const startIdx = (currentPage - 1) * itemsPerPage;
  const endIdx = startIdx + itemsPerPage;

  // Get data for current page
  const currentData = useMemo(() => {
    return inventoryRows?.slice(startIdx, endIdx);
  }, [inventoryRows, startIdx, endIdx]);


  console.log("currentData", currentData)
  // Function to add a new main inventory row
  // Replace your handleAddRow function with:
  const handleAddRow = () => {
    if (isInventoryInteractionDisabled) return;
    const newRow = defaultInventoryRow();
    setInventoryRows((prevRows) => {
      const updatedRows = [...prevRows, newRow];

      // Calculate new page if needed so the new row is visible
      const newTotalPages = Math.ceil(updatedRows.length / itemsPerPage);
      if (currentPage < newTotalPages) {
        setCurrentPage(newTotalPages);
      }

      return updatedRows;
    });
    // Scroll to the new row after it's rendered
    setTimeout(scrollToNewRow, 150);
  };


  const checkAssemblyMinimumQuantity = (
    unitsAvailable,
    unitsRequired,
    minimumQuantity
  ) => {
    const unitsAvailableNum = Number(unitsAvailable) || 0;
    const unitsRequiredNum = Number(unitsRequired) || 0;
    const minimumQuantityNum = Number(minimumQuantity) || 0;

    // If minimumQuantity is 0 or empty, criteria is always met
    if (minimumQuantityNum === 0) return true;

    // Keep live validation consistent with submit-time validation:
    // submit requires `unitsAvailable >= minimumQuantity` (unitsRequired does not affect this).
    void unitsRequiredNum; // reserved for signature compatibility
    return unitsAvailableNum >= minimumQuantityNum;
  };

  const handleInputChange = (rowIndex, field, value) => {
    if (isInventoryInteractionDisabled) return;
    setInventoryRows((prevRows) => {
      const newRows = [...prevRows];

      if (field === "assembly") {
        // Use master (assembly inventory) only so UNITS AVAILABLE updates from database (e.g. 320), not previous saved value (e.g. 330)
        const { unitsAvailable: masterUnitsAvailable, minimumQuantity } =
          getMasterUnitsAvailableForAssembly(value);
        
        // Calculate available units: Master - Already submitted for same assembly (excluding current row)
        let totalRequiredFromOtherRows = 0;
        
        // 1. Check current rows (excluding current row)
        totalRequiredFromOtherRows += newRows.reduce((sum, otherRow, idx) => {
          if (idx === rowIndex) return sum; // Skip current row
          if (otherRow.assembly && 
              otherRow.assembly.trim().toLowerCase() === value.trim().toLowerCase() &&
              otherRow._id && 
              !otherRow._id.includes("temp") &&
              otherRow._id.trim() !== "") {
            const rowRequired = (otherRow.projectDetails || []).reduce(
              (rowSum, project) => rowSum + (Number(project.quantity) || 0),
              0
            );
            return sum + rowRequired;
          }
          return sum;
        }, 0);
        
        // 2. Check saved items (from backend)
        if (items && Array.isArray(items)) {
          const filteredItems = items.filter(
            (item) =>
              item.createdBy === loggedUser?._id &&
              isMatchingSelectedGroup(item.groupid ?? item.group) &&
              !isOptimisticallyDeleted(item?._id)
          );
          
          totalRequiredFromOtherRows += filteredItems.reduce((sum, savedItem) => {
            // Skip if this saved item is the current row (by _id)
            if (newRows[rowIndex]._id && savedItem._id && savedItem._id === newRows[rowIndex]._id) {
              return sum;
            }
            
            if (savedItem.assembly && 
                savedItem.assembly.trim().toLowerCase() === value.trim().toLowerCase()) {
              const savedRequired = (savedItem.projectDetails || []).reduce(
                (rowSum, project) => rowSum + (Number(project.quantity) || 0),
                0
              );
              return sum + savedRequired;
            }
            return sum;
          }, 0);
        }
        
        const masterAvailable = Number(masterUnitsAvailable) || 0;
        const calculatedAvailable = Math.max(0, masterAvailable - totalRequiredFromOtherRows);

        // On assembly select: keep Block Units unselected and show all available projects.
        // Units Required should show totalUnitsrequired before user picks block units.
        const availableForAssembly = getAvailableProjectsForAssembly(value);
        const alreadySavedIds = getAlreadySavedProjectIdsForAssembly(value);
        const otherRowsIds = getAlreadyUsedProjectIdsInOtherRows(value, newRows[rowIndex]?._id);
        const allExcludedIds = new Set([...alreadySavedIds, ...otherRowsIds]);

        const projectsForRow = availableForAssembly.filter((p) => {
          const pid = p.projectId || p._id;
          const qid = p.quotationId;
          const pidExcluded = pid != null && allExcludedIds.has(String(pid));
          const qidExcluded = qid != null && allExcludedIds.has(String(qid));
          return !pidExcluded && !qidExcluded;
        });

        const totalUnitsRequiredForAssembly = projectsForRow.reduce(
          (sum, proj) => sum + (Number(proj.totalUnitsRequired) || Number(proj.quantity) || 0),
          0
        );

        newRows[rowIndex] = {
          ...newRows[rowIndex],
          assembly: value,
          unitsAvailable: calculatedAvailable.toString(), // Master minus already submitted
          minimumQuantity: minimumQuantity,
          projectDetails: [],
          unitsRequired: totalUnitsRequiredForAssembly.toString(),
          actualUnitsRequired: "0",
        };

        // Check minimum quantity validation when assembly changes
        if (newRows[rowIndex].unitsRequired && calculatedAvailable > 0) {
          // Submit-time validation uses master units for NEW/temp rows.
          const isExistingEntry =
            newRows[rowIndex]._id &&
            !String(newRows[rowIndex]._id).includes("temp") &&
            String(newRows[rowIndex]._id).trim() !== "";

          const unitsAvailableForMinCheck = isExistingEntry
            ? calculatedAvailable.toString()
            : String(masterAvailable);

          const isValid = checkAssemblyMinimumQuantity(
            unitsAvailableForMinCheck,
            newRows[rowIndex].unitsRequired,
            minimumQuantity || newRows[rowIndex].minimumQuantity
          );

          if (!isValid && minimumQuantity) {
            enqueueSnackbar(
              `Assembly "${value}": Minimum quantity criteria not met!`,
              {
                variant: "error",
                autoHideDuration: 5000,
                style: { whiteSpace: "pre-line" },
              }
            );
          }
        }
      } else if (field === "unitsRequired") {
        // Update the unitsRequired
        newRows[rowIndex] = {
          ...newRows[rowIndex],
          [field]: value,
        };

        // Recalculate unitsRequiredDetails for all part details
        const unitsRequiredNum = Number(value) || 0;
        newRows[rowIndex].inventoryDetails = newRows[
          rowIndex
        ].inventoryDetails.map((part) => {
          const unitsPerAssembly = Number(part.unitsPerAssemblyDetails) || 0;
          const calculatedUnitsRequired = unitsPerAssembly * unitsRequiredNum;

          return {
            ...part,
            unitsRequiredDetails: calculatedUnitsRequired.toString(),
          };
        });

        // Check minimum quantity validation when unitsRequired changes
        if (
          newRows[rowIndex].minimumQuantity &&
          newRows[rowIndex].unitsAvailable
        ) {
          const isValid = checkAssemblyMinimumQuantity(
            newRows[rowIndex].unitsAvailable,
            value,
            newRows[rowIndex].minimumQuantity
          );

          if (!isValid) {
            enqueueSnackbar(
              `Assembly "${newRows[rowIndex].assembly || ""
              }"Minimum quantity criteria not met!`,
              {
                variant: "error",
                autoHideDuration: 5000,
                style: { whiteSpace: "pre-line" },
              }
            );
          }
        }
      } else if (field === "minimumQuantity") {
        newRows[rowIndex] = {
          ...newRows[rowIndex],
          [field]: value,
        };

        // Check minimum quantity validation when minimumQuantity changes
        if (
          value &&
          newRows[rowIndex].unitsAvailable &&
          newRows[rowIndex].unitsRequired
        ) {
          const isValid = checkAssemblyMinimumQuantity(
            newRows[rowIndex].unitsAvailable,
            newRows[rowIndex].unitsRequired,
            value
          );

          if (!isValid) {
            enqueueSnackbar(
              `Assembly "${newRows[rowIndex].assembly || ""
              }": Minimum quantity criteria not met!`,
              {
                variant: "error",
                autoHideDuration: 5000,
                style: { whiteSpace: "pre-line" },
              }
            );
          }
        }
      } else {
        newRows[rowIndex] = {
          ...newRows[rowIndex],
          [field]: value,
        };
      }

      return newRows;
    });
  };

  const handleSubmit = async () => {
    if (isInventoryInteractionDisabled) return;
    console.log("Submitting inventory:", inventoryRows);
    // Submit only freshly added rows (do not resubmit existing DB rows).
    const freshRowsToSubmit = (inventoryRows || []).filter((row) => {
      const rowId = row?._id;
      const isFreshRow =
        !rowId ||
        String(rowId).includes("temp") ||
        String(rowId).trim() === "";
      const hasAssembly = !!(row?.assembly && String(row.assembly).trim() !== "");
      return isFreshRow && hasAssembly;
    });

    if (freshRowsToSubmit.length === 0) {
      enqueueSnackbar("No new inventory entry to submit.", {
        variant: "info",
      });
      return;
    }

    let hasEmptyFields = false;
    const emptyFieldRows = [];
    freshRowsToSubmit.forEach((item, index) => {
      const emptyFields = [];

      // Check main item fields
      if (!item.assembly || item.assembly.trim() === "") {
        emptyFields.push("Assembly");
      }

      // New rows have Units Available computed for display (master - previous rows); only require it in state for saved rows
      const isNewRow = !item._id || String(item._id).includes("temp") || String(item._id || "").trim() === "";
      const hasUnitsAvailable = item.unitsAvailable != null && String(item.unitsAvailable).trim() !== "";
      if (!hasUnitsAvailable && !(isNewRow && item.assembly && getUnitsAvailableForAssembly(item.assembly).unitsAvailable)) {
        emptyFields.push("Units Available");
      }

      if (!item.unitsRequired || item.unitsRequired.toString().trim() === "") {
        emptyFields.push("Units Required");
      }

      // Block Units: at least one project must be selected
      const hasBlockUnits = item.projectDetails && Array.isArray(item.projectDetails) &&
        item.projectDetails.some((p) => (p.projectId || p._id) && (Number(p.quantity) || 0) > 0);
      if (!hasBlockUnits) {
        emptyFields.push("Block Units");
      }

      if (
        !item.minimumQuantity ||
        item.minimumQuantity.toString().trim() === ""
      ) {
        emptyFields.push("Minimum Quantity");
      }

      // No insufficient-units or subparts validation - user can add subpart data without these checks

      if (emptyFields.length > 0) {
        hasEmptyFields = true;
        emptyFieldRows.push({
          row: index + 1,
          assembly: item.assembly || "Unnamed",
          emptyFields: emptyFields,
        });
      }
    });

    // If there are empty fields, show error with field name(s) and stop
    if (hasEmptyFields) {
      const uniqueFieldNames = [...new Set(emptyFieldRows.flatMap((r) => r.emptyFields))];
      const fieldList = uniqueFieldNames.join(", ");
      enqueueSnackbar(`Please fill the following required fields: ${fieldList}.`, {
        variant: "error",
        autoHideDuration: 8000,
        style: { whiteSpace: "pre-line" },
      });
      return;
    }

    // Submit validation: Units available must not be less than min quantity.
    // Use master inventory values only for new rows.
    // For existing/submitted rows, validate using the row's saved values.
    for (let i = 0; i < freshRowsToSubmit.length; i++) {
      const item = freshRowsToSubmit[i];
      if (!item.assembly || item.assembly.trim() === "") continue;
      const isExistingEntry =
        item._id &&
        !String(item._id).includes("temp") &&
        String(item._id).trim() !== "";
      const {
        unitsAvailable: masterUnitsAvailable,
        minimumQuantity: masterMinimumQuantity,
      } = getMasterUnitsAvailableForAssembly(item.assembly);
      const unitsAvailableStr =
        isExistingEntry
          ? (item.unitsAvailable != null && String(item.unitsAvailable).trim() !== ""
            ? String(item.unitsAvailable).trim()
            : (masterUnitsAvailable != null && String(masterUnitsAvailable).trim() !== ""
              ? String(masterUnitsAvailable).trim()
              : "0"))
          : (masterUnitsAvailable != null && String(masterUnitsAvailable).trim() !== ""
            ? String(masterUnitsAvailable).trim()
            : (item.unitsAvailable != null && String(item.unitsAvailable).trim() !== ""
              ? String(item.unitsAvailable).trim()
              : "0"));
      const minQtyStr =
        isExistingEntry
          ? (item.minimumQuantity != null && String(item.minimumQuantity).trim() !== ""
            ? String(item.minimumQuantity).trim()
            : (masterMinimumQuantity != null && String(masterMinimumQuantity).trim() !== ""
              ? String(masterMinimumQuantity).trim()
              : "0"))
          : (masterMinimumQuantity != null && String(masterMinimumQuantity).trim() !== ""
            ? String(masterMinimumQuantity).trim()
            : (item.minimumQuantity != null && String(item.minimumQuantity).trim() !== ""
              ? String(item.minimumQuantity).trim()
              : "0"));
      const unitsAvailableNum = Number(unitsAvailableStr) || 0;
      const minQuantityNum = Number(minQtyStr) || 0;
      if (minQuantityNum > 0 && unitsAvailableNum < minQuantityNum) {
        enqueueSnackbar("Units available is less than the min quantity", {
          variant: "error",
          autoHideDuration: 5000,
        });
        return;
      }
    }

    console.log("gdfhdfh", freshRowsToSubmit);
    const updatedInventory = freshRowsToSubmit.map((item) => {
      // Check if _id exists and contains "123"
      if (item._id && item._id.includes("temp")) {
        // Return a new object with blank _id (keeping all other properties)
        return {
          ...item,
          _id: "", // Or use null or undefined if preferred
        };
      }
      // Return the original object unchanged
      return item;
    });
    // If validation passes, proceed with data cleaning and submission
    const cleanedData = updatedInventory.map((item, index) => {
      // Create a new object without mutating the original
      // IMPORTANT: Preserve projectDetails from the item
      const cleanedItem = { 
        ...item,
        projectDetails: item.projectDetails || [], // Ensure projectDetails are preserved
      };
      
      console.log("Preparing to save item:", {
        _id: item._id,
        assembly: item.assembly,
        projectDetailsBeforeClean: item.projectDetails,
        projectDetailsLength: (item.projectDetails || []).length,
      });
      
      // For save: use master (assembly inventory) for current DB value; saved rows keep item.unitsAvailable
      const { unitsAvailable: masterUnitsAvailable, minimumQuantity: masterMinimumQuantity } =
        getMasterUnitsAvailableForAssembly(item.assembly);
      const masterAvailable = masterUnitsAvailable ? Number(masterUnitsAvailable) : (Number(item.unitsAvailable) || 0);
      
      // Units Available: save the current DB value for this row (what user sees).
      // Existing entry: keep saved value. New entry: use current master/DB value (already updated after prior saves).
      const isExistingEntry = cleanedItem._id && !String(cleanedItem._id).includes("temp") && String(cleanedItem._id).trim() !== "";
      if (isExistingEntry) {
        cleanedItem.unitsAvailable = Number(item.unitsAvailable) || masterAvailable;
      } else {
        cleanedItem.unitsAvailable = Math.max(0, masterAvailable);
      }
      // Keep table heading value in dedicated backend field.
      cleanedItem.totalUnitsrequired = Number(item.unitsRequired) || 0;
      // Calculate unitsRequired from projectDetails (sum of all selected project quantities)
      const calculatedUnitsRequired = (cleanedItem.projectDetails || []).reduce(
        (sum, project) => sum + (Number(project.quantity) || 0),
        0
      );
      cleanedItem.unitsRequired = calculatedUnitsRequired; // Always calculate from projectDetails
      // For existing entry: send previousUnitsRequired so backend subtracts only the delta from master assembly Units Available (not full new total)
      if (isExistingEntry && items && Array.isArray(items)) {
        const savedItem = items.find((s) => s._id && String(s._id) === String(cleanedItem._id));
        if (savedItem) {
          const prevRequired = savedItem.unitsRequired != null && String(savedItem.unitsRequired).trim() !== ""
            ? Number(savedItem.unitsRequired)
            : (savedItem.projectDetails || []).reduce((sum, p) => sum + (Number(p.quantity) || Number(p.units) || 0), 0);
          cleanedItem.previousUnitsRequired = prevRequired;
        }
      }
      // Always use minimumQuantity from master inventory
      cleanedItem.minimumQuantity = masterMinimumQuantity ? Number(masterMinimumQuantity) : (Number(cleanedItem.minimumQuantity) || 0);
      
      // Calculate subpartsrequiredquantity from the latest values:
      // if unitsRequired > unitsAvailable, send (difference + minimumQuantity).
      // IMPORTANT: Always base this on the current state to avoid using any stale value.
      const unitsRequiredNum = Number(cleanedItem.unitsRequired) || 0;
      const unitsAvailableNum = Number(cleanedItem.unitsAvailable) || 0;
      const minimumQuantityNum = Number(cleanedItem.minimumQuantity) || 0;
      if (unitsRequiredNum > unitsAvailableNum) {
        const difference = unitsRequiredNum - unitsAvailableNum;
        cleanedItem.subpartsrequiredquantity = difference + minimumQuantityNum;
      } else {
        // No additional subparts required for this assembly – clear any old value
        delete cleanedItem.subpartsrequiredquantity;
      }
      
      // Ensure projectDetails are preserved and have all required fields when saving
      // CRITICAL: projectDetails must be saved to backend so they persist after submission
      if (!cleanedItem.projectDetails || !Array.isArray(cleanedItem.projectDetails)) {
        cleanedItem.projectDetails = [];
      }
      
      // Build projectDetails for POST /inventorydata: backend expects projectid + units (no projectName; name shown from project lookup)
      if (cleanedItem.projectDetails.length > 0) {
        cleanedItem.projectDetails = cleanedItem.projectDetails.map((pd) => ({
          projectId: pd.projectId || pd._id,
          quantity: Number(pd.quantity) || 0,
        }));
        console.log("Saving projectDetails:", {
          assembly: item.assembly,
          projectDetailsCount: cleanedItem.projectDetails.length,
          projectDetails: cleanedItem.projectDetails,
        });
      } else {
        console.log("WARNING: projectDetails is empty for assembly:", item.assembly);
      }
      
      // CRITICAL: Always ensure projectDetails is an array (even if empty) so it's saved to backend
      if (!Array.isArray(cleanedItem.projectDetails)) {
        cleanedItem.projectDetails = [];
      }
      
      // Remove empty _id fields
      if (!cleanedItem._id || cleanedItem._id.trim() === "") {
        delete cleanedItem._id;
      }
      // Clean nested inventoryDetails
      if (Array.isArray(cleanedItem.inventoryDetails)) {
        cleanedItem.inventoryDetails = cleanedItem.inventoryDetails.map(
          (detail) => {
            const cleanedDetail = { ...detail };

            if (cleanedDetail._id && cleanedDetail._id.includes("temp")) {
              cleanedDetail._id = ""; // Set to empty string
            }
            // Convert numeric fields to numbers
            cleanedDetail.unitsAvailableDetails =
              Number(cleanedDetail.unitsAvailableDetails) || 0;
            cleanedDetail.unitsRequiredDetails =
              Number(cleanedDetail.unitsRequiredDetails) || 0;
            cleanedDetail.unitsPerAssemblyDetails =
              Number(cleanedDetail.unitsPerAssemblyDetails) || 0;
            cleanedDetail.unitsMinimumQuantityDetails =
              Number(cleanedDetail.unitsMinimumQuantityDetails) || 0;
            cleanedDetail.threshold = Number(cleanedDetail.threshold) || 0;

            if (!cleanedDetail._id || cleanedDetail._id.trim() === "") {
              delete cleanedDetail._id;
            }
            return cleanedDetail;
          }
        );
      }

      // CRITICAL: Final check - Ensure projectDetails are included and properly formatted before saving
      // This is the last chance to ensure projectDetails are saved to backend
      if (!cleanedItem.projectDetails || !Array.isArray(cleanedItem.projectDetails)) {
        // If projectDetails are missing, try to get from original item
        if (item.projectDetails && Array.isArray(item.projectDetails)) {
          cleanedItem.projectDetails = item.projectDetails;
        } else {
          cleanedItem.projectDetails = [];
        }
      }
      
      // Ensure projectDetails array is not empty and has valid data
      if (cleanedItem.projectDetails.length > 0) {
        cleanedItem.projectDetails = cleanedItem.projectDetails.filter(pd => {
          return (pd.projectId || pd._id) && (Number(pd.quantity) || 0) > 0;
        });
      }
      
      console.log("Final cleanedItem before save:", {
        _id: cleanedItem._id,
        assembly: cleanedItem.assembly,
        unitsRequired: cleanedItem.unitsRequired,
        projectDetails: cleanedItem.projectDetails,
        projectDetailsLength: cleanedItem.projectDetails.length,
        hasProjectDetails: !!cleanedItem.projectDetails,
        projectDetailsType: Array.isArray(cleanedItem.projectDetails) ? 'array' : typeof cleanedItem.projectDetails,
      });
      
      // POST /inventorydata: projectDetails array with projectid (lowercase) and units only; projectName is resolved from projectid when loading
      const payloadProjectDetails = (cleanedItem.projectDetails || []).map((pd) => ({
        projectid: (pd.projectId || pd._id || "").toString(),
        units: Number(pd.quantity) || 0,
      })).filter((pd) => pd.projectid && pd.units > 0);

      const submitGroupId = resolveInventorySubmitGroupId(item);
      const finalItem = {
        ...cleanedItem,
        projectDetails: payloadProjectDetails,
      };
      delete finalItem.group;
      if (submitGroupId) {
        finalItem.groupid = submitGroupId;
      }

      return finalItem;
    });

    console.log("Final cleanedData to save:", cleanedData.map(item => ({
      _id: item._id,
      assembly: item.assembly,
      unitsRequired: item.unitsRequired,
      projectDetailsCount: (item.projectDetails || []).length,
      projectDetails: item.projectDetails,
    })));

    // Before sending: for any item that currently requires extra subparts (based on latest units),
    // check HardwareStoreData (filter by group then assemblyname);
    // if subparts not available for this assembly, show error and abort.
    // Also validate order quantity: subpartsrequiredquantity must be greater than orderquantity.
    let hardwareStoreData = [];
    if (hardwareData) {
      if (Array.isArray(hardwareData)) {
        hardwareStoreData = hardwareData;
      } else if (typeof hardwareData === "object" && hardwareData.data && Array.isArray(hardwareData.data)) {
        hardwareStoreData = hardwareData.data;
      }
    }
    for (let i = 0; i < cleanedData.length; i++) {
      const item = cleanedData[i];
      const rowBeforeClean = freshRowsToSubmit[i];
      // Recompute required subparts from the final payload values to avoid any stale state
      const unitsRequiredNum = Number(item.unitsRequired) || 0;
      const unitsAvailableNum = Number(item.unitsAvailable) || 0;
      const minimumQuantityNum = Number(item.minimumQuantity) || 0;
      const subpartsRequired =
        unitsRequiredNum > unitsAvailableNum
          ? unitsRequiredNum - unitsAvailableNum + minimumQuantityNum
          : 0;
      if (subpartsRequired <= 0) continue;
      const assemblyName = (item.assembly || "").trim();
      if (!assemblyName) continue;
      const group = (
        selectedInventoryGroupName ||
        String(rowBeforeClean?.group || "").trim()
      ).trim();
      // Filter by group first, then by assembly name (match Hardware.jsx behavior)
      let filteredByGroup = group
        ? hardwareStoreData.filter(
            (h) =>
              (h.group || "").trim().toLowerCase() === group.toLowerCase()
          )
        : hardwareStoreData;
      let linkedSubparts = filteredByGroup.filter(
        (h) =>
          (h.assemblyname || "").trim().toLowerCase() ===
          assemblyName.toLowerCase()
      );
      // If no subparts found with group filter, fallback: match by assembly name only (in case group differs or is missing in hardware store)
      if (linkedSubparts.length === 0) {
        linkedSubparts = hardwareStoreData.filter(
          (h) =>
            (h.assemblyname || "").trim().toLowerCase() ===
            assemblyName.toLowerCase()
        );
      }
      // For new entries: subparts are "available" if at least one subpart record exists for this assembly
      const hasEnough = linkedSubparts.length > 0;
      if (!hasEnough) {
        enqueueSnackbar("Sub parts is not available for associated/selected assembly", {
          variant: "error",
        });
        return;
      }

      // Validate against minimum order quantity from hardware subparts.
      // Proceed only when required subparts quantity is greater than orderquantity.
      const isOrderQtyValid = linkedSubparts.every((subpart) => {
        const orderQuantity = Number(subpart.orderquantity) || 0;
        return subpartsRequired > orderQuantity;
      });
      if (!isOrderQtyValid) {
        enqueueSnackbar("Sub parts ,minmum ordering quantiy is more plz add more", {
          variant: "error",
        });
        return;
      }
    }

    SetisLoading(true);
    try {
      await dispatch(createOrUpdateInventoryData(cleanedData));
      enqueueSnackbar("Inventory added successfully.", {
        variant: "success",
      });
      triggerSavedEntriesPreview(freshRowsToSubmit);
    } catch (error) {
      console.error("Failed to create inventory:", error);
      enqueueSnackbar("Failed to save inventory", {
        variant: "error",
      });
    } finally {
      SetisLoading(false);
      dispatch(getInventoryByCreatedBy());
      refresh();
    }
  };

  const handleDeleteRow = (id, rowIndex) => {
    if (isInventoryInteractionDisabled) return;
    const actualIndex = startIdx + rowIndex;
    setDeleteIndex(actualIndex);

    if (id && !id.includes("temp")) {
      setRowToDelete(id);
      setIsModalOpen(true);
    } else {
      setIsModal(true);
    }
  };

  const confirmDelete = async () => {
    if (isInventoryInteractionDisabled) {
      setIsModalOpen(false);
      setIsModal(false);
      setRowToDelete(null);
      setDeleteIndex(null);
      return;
    }
    if (id) {
      SetisLoading(true);
      try {
        const deletingId = String(id);
        setOptimisticallyDeletedIds((prev) =>
          prev.includes(deletingId) ? prev : [...prev, deletingId]
        );
        const resultAction = await dispatch(deleteInventoryData(id));
        const isRejected = resultAction?.meta?.requestStatus === "rejected";

        if (isRejected) {
          const payloadMessage =
            resultAction?.payload?.message ||
            resultAction?.payload?.error ||
            resultAction?.error?.message ||
            "";
          const isAlreadyDeletedOrMissing =
            typeof payloadMessage === "string" &&
            payloadMessage.toLowerCase().includes("inventory data not found");

          if (isAlreadyDeletedOrMissing) {
            // Backend row already does not exist; keep it hidden in UI.
            enqueueSnackbar("Row removed (already deleted on server).", {
              variant: "info",
            });
          } else {
            // Rollback optimistic hide for real delete failures.
            setOptimisticallyDeletedIds((prev) =>
              prev.filter((existingId) => existingId !== deletingId)
            );
            enqueueSnackbar("Failed to delete row", {
              variant: "error",
            });
          }
        } else {
          enqueueSnackbar("Row Deleted Successfully!", {
            variant: "success",
          });
        }
      } catch (error) {
        const deletingId = String(id);
        const errorMessage =
          error?.response?.data?.message ||
          error?.message ||
          "";
        const isAlreadyDeletedOrMissing =
          typeof errorMessage === "string" &&
          errorMessage.toLowerCase().includes("inventory data not found");

        if (isAlreadyDeletedOrMissing) {
          // Backend row already does not exist; keep it hidden in UI.
          enqueueSnackbar("Row removed (already deleted on server).", {
            variant: "info",
          });
        } else {
          setOptimisticallyDeletedIds((prev) =>
            prev.filter((existingId) => existingId !== deletingId)
          );
          console.error("Failed to delete row:", error);
          enqueueSnackbar("Failed to delete row", {
            variant: "error",
          });
        }
      } finally {
        SetisLoading(false);
        setRowToDelete(null);
        setIsModalOpen(false);
        dispatch(getInventoryByCreatedBy());
        setInventoryRows((prevRows) => {
          const newRows = [...prevRows];
          newRows.splice(deleteIndex, 1);
          if (newRows.length === 0) {
            return [
              {
                _id: generateRowId(),
                assembly: "",
                unitsAvailable: "",
                unitsRequired: "",
                minimumQuantity: "",
                inventoryDetails: [
                  {
                    _id: "",
                    subparts: "",
                    unitsAvailableDetails: "",
                    unitsRequiredDetails: "",
                    unitsPerAssemblyDetails: "",
                    unitsMinimumQuantityDetails: "",
                    supplier: "",
                    orderingStatus: "",
                    threshold: "",
                  },
                ],
              },
            ];
          }
          return newRows;
        });
      }
    } else {
      // For rows without ID (new rows not saved yet)
      setInventoryRows((prevRows) => {
        const newRows = [...prevRows];
        newRows.splice(deleteIndex, 1);
        if (newRows.length === 0) {
          return [
            {
              _id: generateRowId(),
              assembly: "",
              unitsAvailable: "",
              unitsRequired: "",
              minimumQuantity: "",
              inventoryDetails: [
                {
                  _id: "",
                  subparts: "",
                  unitsAvailableDetails: "",
                  unitsRequiredDetails: "",
                  unitsPerAssemblyDetails: "",
                  unitsMinimumQuantityDetails: "",
                  supplier: "",
                  orderingStatus: "",
                  threshold: "",
                },
              ],
            },
          ];
        }
        return newRows;
      });
      setIsModal(false);
      enqueueSnackbar("Row Deleted Successfully", {
        variant: "success",
      });
    }
    setDeleteIndex(null);
  };
  useEffect(() => {
    if (!optimisticallyDeletedIds.length || !Array.isArray(items)) return;
    const currentItemIds = new Set(
      items
        .map((item) => item?._id)
        .filter((rowId) => rowId != null)
        .map((rowId) => String(rowId))
    );
    setOptimisticallyDeletedIds((prev) =>
      prev.filter((deletedId) => currentItemIds.has(deletedId))
    );
  }, [items, optimisticallyDeletedIds]);
  const getActualRowIndex = (pageRowIndex) => {
    return startIdx + pageRowIndex;
  };

  // Refs for scrollable container and last row (to scroll into view when adding)
  const tableContainerRef = React.useRef(null);
  const lastRowRef = React.useRef(null);

  // Scroll to the newly added row after add and re-render
  const scrollToNewRow = useCallback(() => {
    requestAnimationFrame(() => {
      lastRowRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    });
  }, []);

  return (
    <>
      {isLoading ? <AdminLoader isLoading={isLoading} /> : <></>}
      <div className="pt-6">
        <div className="mr-4 p-4 min-h-[20rem] bg-white rounded-md border border-gray-200 shadow-lg">
          <div className="flex justify-start py-1 align-middle items-center pr-8 mb-4">
            <h1 className="text-2xl font-semibold">
              {SidebarInventoryGroup ? `Inventory : ${selectedInventoryGroupName || SidebarInventoryGroup}` : "Inventory"}
            </h1>
          </div>

          <div
            ref={tableContainerRef}
            className="w-full min-h-[24rem] max-h-[85vh] overflow-auto"
          >
            {/* Header row */}
            <div className="flex sticky top-0 h-12 w-full text-xs z-[700] font-semibold uppercase">
              <div className="p-2 bg-[#e8e8e8] flex-shrink-0 w-16 flex justify-center items-center text-center border border-gray-300">
                SR. NO
              </div>
              <div className="p-2 bg-[#e8e8e8] flex-1 flex justify-center items-center text-center border border-gray-300">
                ASSEMBLY NAME
              </div>
              <div className="p-2 bg-[#e8e8e8] flex-1 flex justify-center items-center text-center border border-gray-300">
                UNITS REQUIRED
              </div>
              <div className="p-2 bg-[#e8e8e8] flex-1 flex justify-center items-center text-center border border-gray-300">
                BLOCK UNITS
              </div>
              <div className="p-2 bg-[#e8e8e8] flex-1 flex justify-center items-center text-center border border-gray-300">
                UNITS AVAILABLE
              </div>
              <div className="p-2 bg-[#e8e8e8] flex-1 flex justify-center items-center text-center border border-gray-300">
                MIN. QUANTITY
              </div>
              <div className="p-2 bg-[#e8e8e8] flex-1 flex justify-center items-center text-center border border-gray-300">
                ACTIONS
              </div>
            </div>

            {/* Data rows */}
            <div className="w-full">
              {currentData?.map((row, rowIndex) => {
                const rowNumber = startIdx + rowIndex + 1;
                const actualRowIndex = getActualRowIndex(rowIndex);
                const currentAssemblyLower = (row.assembly || "").trim().toLowerCase();
                const usedAssembliesInSameGroup = new Set(
                  (inventoryRows || [])
                    .filter((r, idx) => {
                      if (!r || idx === actualRowIndex) return false;
                      if (SidebarInventoryGroup && !isMatchingSelectedGroup(r.groupid ?? r.group)) return false;
                      return !!(r.assembly && String(r.assembly).trim() !== "");
                    })
                    .map((r) => String(r.assembly).trim().toLowerCase())
                );
                const assemblyOptionsForRow = (assemblyOptions || []).filter((assemblyName) => {
                  const assemblyLower = String(assemblyName || "").trim().toLowerCase();
                  if (!assemblyLower) return false;
                  if (assemblyLower === currentAssemblyLower) return true;
                  return !usedAssembliesInSameGroup.has(assemblyLower);
                });

                return (
                  <div
                    key={`row-${actualRowIndex}`}
                    ref={rowIndex === currentData.length - 1 ? lastRowRef : null}
                    className="inventry group w-full text-xs font-medium"
                  >
                    <div className="flex w-full">
                    {/* Row number */}
                    <div className="p-2 border-b border-gray-300 flex-shrink-0 w-16 flex justify-center items-center text-center">
                      {rowNumber}
                    </div>

                    {/* Assembly input */}
                    <div className="p-2 border-b border-gray-300 flex-1 flex justify-center items-center text-center">
                      <select
                        className={`w-full text-xs border rounded-md p-2 shadow-md text-start overflow-hidden outline-none border-gray-400 ${isInventoryInteractionDisabled ? "bg-[#f3f4f6] cursor-not-allowed pointer-events-none text-[#9ca3af]" : "cursor-pointer bg-white"}`}
                        value={row.assembly}
                        disabled={isInventoryInteractionDisabled}
                        onChange={(e) =>
                          handleInputChange(
                            actualRowIndex,
                            "assembly",
                            e.target.value
                          )
                        }
                      >
                        <option value="" disabled hidden>
                          Select Assembly
                        </option>
                        {assemblyOptionsForRow.length > 0 ? (
                          assemblyOptionsForRow.map((assemblyName, idx) => (
                            <option key={idx} value={assemblyName}>
                              {assemblyName}
                            </option>
                          ))
                        ) : (
                          <option disabled>No Assembly Found</option>
                        )}
                      </select>
                    </div>
                    {/* Units Required - totalUnitsrequired before selection; selected actualunitrequired after selection */}
                    <div className="p-2 border-b border-gray-300 flex-1 flex justify-center items-center text-center">
                      <input
                        type="text"
                        className={`border w-full text-xs border-gray-400 rounded-md p-2 shadow-md text-center overflow-hidden outline-none ${isInventoryInteractionDisabled ? "bg-[#f3f4f6] text-[#9ca3af] cursor-not-allowed" : "bg-white"}`}
                        value={row.unitsRequired != null && String(row.unitsRequired).trim() !== "" ? String(row.unitsRequired).trim() : "0"}
                        readOnly
                        title="Shows totalUnitsrequired first, then selected actualunitrequired"
                      />
                    </div>
                    {/* Block Units */}
                    <BlockUnitsDropdown
                      rowIndex={actualRowIndex}
                      row={row}
                      assemblyName={row.assembly}
                      projectDetails={row.projectDetails || []}
                      availableProjects={(() => {
                        const available = getAvailableProjectsForAssembly(row.assembly);
                        // Exclude projects already used for this assembly: group -> assembly -> project (projectId / quotationId)
                        const alreadySavedIds = getAlreadySavedProjectIdsForAssembly(row.assembly);
                        const otherRowsIds = getAlreadyUsedProjectIdsInOtherRows(row.assembly, row._id);
                        const allExcludedIds = new Set([...alreadySavedIds, ...otherRowsIds]);
                        if (allExcludedIds.size === 0) return available;
                        return available.filter((p) => {
                          const pid = p.projectId || p._id;
                          const qid = p.quotationId;
                          const pidExcluded = pid != null && allExcludedIds.has(String(pid));
                          const qidExcluded = qid != null && allExcludedIds.has(String(qid));
                          return !pidExcluded && !qidExcluded;
                        });
                      })()}
                      allRowsProjectDetails={inventoryRows.map(r => r.projectDetails || [])}
                      projectsList={projects}
                      disabled={isInventoryInteractionDisabled}
                      onProjectSelect={(project) => {
                        if (isInventoryInteractionDisabled) return;
                        setInventoryRows((prevRows) => {
                          const newRows = [...prevRows];
                          if (!newRows[actualRowIndex]) return newRows;
                          const existingDetails = newRows[actualRowIndex].projectDetails || [];
                          const newProjectId = project.projectId || project.projectid || project._id;
                          const newQuotationId = project.quotationId || project.quotationid;
                          const exists = existingDetails.some((p) => {
                            const existingProjectId = p.projectId || p.projectid || p._id;
                            const existingQuotationId = p.quotationId || p.quotationid;
                            const sameProjectId =
                              existingProjectId != null &&
                              newProjectId != null &&
                              String(existingProjectId) === String(newProjectId);
                            const sameQuotationId =
                              existingQuotationId != null &&
                              newQuotationId != null &&
                              String(existingQuotationId) === String(newQuotationId);
                            return sameProjectId || sameQuotationId;
                          });
                          if (exists) return newRows;

                          // Would-be new state when this project is added
                          const updatedDetails = [...existingDetails, project];
                          const updatedActualUnitsRequired = updatedDetails.reduce(
                            (sum, p) => sum + (Number(p.quantity) || 0),
                            0
                          );
                          // Preserve unitsAvailable (and all other fields) when editing - do not recalculate for saved rows
                          newRows[actualRowIndex] = {
                            ...newRows[actualRowIndex],
                            projectDetails: updatedDetails,
                            actualUnitsRequired: updatedActualUnitsRequired.toString(),
                          };
                          return newRows;
                        });
                      }}
                      onProjectRemove={(projectId) => {
                        if (isInventoryInteractionDisabled) return;
                        setInventoryRows((prevRows) => {
                          const newRows = [...prevRows];
                          if (newRows[actualRowIndex]) {
                            const existingDetails = newRows[actualRowIndex].projectDetails || [];
                            const updatedDetails = existingDetails.filter(
                              (p) =>
                                String(p.projectId || p.projectid || p._id) !== String(projectId)
                            );
                            const updatedActualUnitsRequired = updatedDetails.reduce(
                              (sum, p) => sum + (Number(p.quantity) || 0),
                              0
                            );
                            // Preserve unitsAvailable (and all other fields) when editing - do not change for saved rows
                            newRows[actualRowIndex] = {
                              ...newRows[actualRowIndex],
                              projectDetails: updatedDetails,
                              actualUnitsRequired: updatedActualUnitsRequired.toString(),
                            };
                          }
                          return newRows;
                        });
                      }}
                    />
                    {/* Units Available - saved per entry: show saved value for saved rows; calculated for new rows */}
                    <div className="p-2 border-b border-gray-300 flex-1 flex justify-center items-center text-center">
                      <input
                        type="text"
                        className={`border w-full text-xs border-gray-400 rounded-md p-2 shadow-md text-center overflow-hidden outline-none ${isInventoryInteractionDisabled ? "bg-[#f3f4f6] text-[#9ca3af] cursor-not-allowed" : "bg-white"}`}
                        value={(() => {
                          if (!row.assembly) return "0";
                          // For saved entries: use inventory (saved).unitsAvailable so saved data matches DB
                          const isSavedEntry = row._id && !String(row._id).includes("temp") && String(row._id).trim() !== "";
                          if (isSavedEntry && row.unitsAvailable != null && String(row.unitsAvailable).trim() !== "") {
                            return String(row.unitsAvailable).trim();
                          }
                          // When assembly selected (new/unsaved row): show current DB value from assembly inventory (master), not assemblyinventory cached value
                          const { unitsAvailable: masterUnitsAvailable } = getMasterUnitsAvailableForAssembly(row.assembly);
                          const masterAvailable = Number(masterUnitsAvailable) || 0;
                          return Math.max(0, masterAvailable).toString();
                        })()}
                        readOnly
                        title="Units Available: saved per entry for saved rows; calculated for new rows"
                      />
                    </div>
                    {/* Min. Quantity */}
                    <div className="p-2 border-b border-gray-300 flex-1 flex justify-center items-center text-center">
                      <input
                        type="text"
                        className={`border w-full text-xs border-gray-400 rounded-md p-2 shadow-md text-center overflow-hidden outline-none ${isInventoryInteractionDisabled ? "bg-[#f3f4f6] text-[#9ca3af] cursor-not-allowed" : "bg-white"}`}
                        value={row.minimumQuantity || "0"}
                        readOnly
                      />
                    </div>
                    {/* Actions */}
                    <div className="p-2 border-b border-gray-300 flex-1 flex justify-center items-center text-center gap-2">
                      <button
                        onClick={() => handleDeleteRow(row._id, rowIndex)}
                        disabled={isInventoryInteractionDisabled}
                        className={isInventoryInteractionDisabled ? "opacity-60 cursor-not-allowed pointer-events-none" : "cursor-pointer hover:opacity-70"}
                        title="Delete"
                      >
                        <Trash2 size={18} className={isInventoryInteractionDisabled ? "text-[#9ca3af]" : "text-blue-500"} />
                      </button>
                    </div>
                  </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Footer buttons */}
          <div className="mt-4">
            <div className="w-full flex justify-between items-center py-2">
              <div className="flex items-center gap-3">
                <button
                  onClick={handleAddRow}
                  disabled={isInventoryInteractionDisabled}
                  className={`flex items-center gap-2 px-4 py-2 border rounded-md transition-colors ${isInventoryInteractionDisabled ? "opacity-60 cursor-not-allowed pointer-events-none border-gray-300 text-[#9ca3af] bg-gray-100" : "border-blue-500 text-blue-500 bg-white hover:bg-blue-50"}`}
                  title="Add New Inventory Row"
                >
                  <AddIcon fontSize="small" />
                  <span className="text-xs">Add Assembly</span>
                </button>
                {/* <span className="text-xs text-gray-600">
                  Showing {totalItems ?? 0} Inventory data
                </span> */}
              </div>
              <div>
                {(() => {
                  const hasAssemblyRow = inventoryRows.some((row) => row.assembly && String(row.assembly).trim() !== "");
                  const isSubmitDisabled = isInventoryInteractionDisabled || !hasAssemblyRow;
                  return (
                    <button
                      onClick={handleSubmit}
                      disabled={isSubmitDisabled}
                      className={`text-xs py-2 px-4 rounded-md transition-colors ${
                        isInventoryInteractionDisabled
                          ? "opacity-60 cursor-not-allowed pointer-events-none bg-gray-300 text-[#9ca3af]"
                          : hasAssemblyRow
                          ? "bg-[#0f61e6] hover:bg-blue-400 text-white"
                          : "bg-gray-300 text-gray-500 cursor-not-allowed"
                      }`}
                    >
                      Submit
                    </button>
                  );
                })()}
              </div>
            </div>
          </div>

          {/* Inventory data count at bottom */}
          <div className="mt-2 text-xs text-gray-600">
            {totalItems
              ? `Showing ${Math.min(startIdx + 1, totalItems)}-${Math.min(endIdx, totalItems)} of ${totalItems} Inventory data`
              : "Showing 0 Inventory data"}
          </div>
        </div>
      </div>

      {/* Modals */}
      {isModalOpen && (
        <div className="fixed inset-0 flex items-center justify-center z-[1400] bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-lg p-6 w-[90%] max-w-md">
            <h2 className="text-xl font-semibold mb-4 text-center">
              Confirm Deletion
            </h2>
            <p className="text-gray-700 mb-6 text-center">
              Are you sure you want to delete this row?
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setIsModalOpen(false);
                  setRowToDelete(null);
                }}
                className="px-4 py-2 rounded-md border border-gray-300 bg-gray-100 hover:bg-gray-200 text-gray-700"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                className="px-4 py-2 rounded-md bg-red-600 hover:bg-red-700 text-white font-semibold"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {isModal && (
        <div className="fixed inset-0 flex items-center justify-center z-[1400] bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-lg p-6 w-[90%] max-w-md">
            <h2 className="text-xl font-semibold mb-4 text-center">
              Confirm Deletion
            </h2>
            <p className="text-gray-700 mb-6 text-center">
              Are you sure you want to delete this row?
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setIsModal(false);
                  setDeleteIndex(null);
                }}
                className="px-4 py-2 rounded-md border border-gray-300 bg-gray-100 hover:bg-gray-200 text-gray-700"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                className="px-4 py-2 rounded-md bg-red-600 hover:bg-red-700 text-white font-semibold"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Inventory;
